import { useTranslation } from "react-i18next";
import { Sparkles, Wand2, MapPin } from "lucide-react";
import type { OnboardingMode } from "../../api/types";
import { useSetOnboardingMode } from "../../hooks/useOnboarding";

interface Props {
  /** Called after the mode is successfully persisted on the server. */
  onPicked: (mode: OnboardingMode) => void;
}

interface ModeCard {
  mode: OnboardingMode;
  icon: typeof Sparkles;
  iconClass: string;
  titleKey: "onboarding.modeModal.blank.title" | "onboarding.modeModal.samples.title" | "onboarding.modeModal.guided.title";
  bodyKey: "onboarding.modeModal.blank.body" | "onboarding.modeModal.samples.body" | "onboarding.modeModal.guided.body";
}

const CARDS: ModeCard[] = [
  {
    mode: "blank",
    icon: MapPin,
    iconClass: "text-slate-400",
    titleKey: "onboarding.modeModal.blank.title",
    bodyKey: "onboarding.modeModal.blank.body",
  },
  {
    mode: "samples",
    icon: Sparkles,
    iconClass: "text-amber-400",
    titleKey: "onboarding.modeModal.samples.title",
    bodyKey: "onboarding.modeModal.samples.body",
  },
  {
    mode: "guided",
    icon: Wand2,
    iconClass: "text-blue-400",
    titleKey: "onboarding.modeModal.guided.title",
    bodyKey: "onboarding.modeModal.guided.body",
  },
];

/** Non-dismissible modal shown after a business is created (and as a fallback
 * on the dashboard if the user lands without an onboarding_mode). The user
 * MUST pick one before continuing — the modal has no close affordance and
 * Escape is not wired. */
export function OnboardingModeModal({ onPicked }: Props) {
  const { t } = useTranslation();
  const setMode = useSetOnboardingMode();

  const handlePick = (mode: OnboardingMode) => {
    if (setMode.isPending) return;
    setMode.mutate(
      { mode },
      {
        onSuccess: () => onPicked(mode),
      },
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-mode-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
      <div className="relative w-full max-w-3xl rounded-3xl bg-slate-900/90 border border-slate-700/80 shadow-2xl shadow-black/50 p-7 sm:p-9">
        <h2
          id="onboarding-mode-title"
          className="text-2xl font-bold text-white text-center"
        >
          {t("onboarding.modeModal.title")}
        </h2>
        <p className="mt-2 text-sm text-slate-400 text-center">
          {t("onboarding.modeModal.subtitle")}
        </p>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.mode}
                type="button"
                onClick={() => handlePick(c.mode)}
                disabled={setMode.isPending}
                aria-label={t(c.titleKey)}
                data-mode={c.mode}
                className="group flex flex-col items-start gap-3 rounded-2xl border border-slate-700 bg-slate-800/60 p-5 text-left transition-colors hover:border-blue-500/60 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900/60 ${c.iconClass}`}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="text-base font-semibold text-white">{t(c.titleKey)}</h3>
                <p className="text-xs leading-relaxed text-slate-400">{t(c.bodyKey)}</p>
              </button>
            );
          })}
        </div>

        {setMode.isPending && (
          <p
            className="mt-5 text-center text-xs text-slate-400"
            aria-live="polite"
          >
            {t("onboarding.modeModal.saving")}
          </p>
        )}
      </div>
    </div>
  );
}
